import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback } from 'react'
import { toast } from 'sonner'

import type { OrganizationRoleKey } from '@/components/org-settings/members/members.interface'

import type { ClerkMembershipsResource } from './members-clerk.types'

interface UseMemberOperationsProps {
  memberships: ClerkMembershipsResource | null
  currentUserId: string | null
}

interface UseMemberOperationsReturn {
  handleRoleChange: (memberId: string, memberRole: OrganizationRoleKey) => Promise<void>
  handleRemoveMember: (memberId: string) => Promise<void>
}

export const useMemberOperations = ({
  memberships,
  currentUserId,
}: UseMemberOperationsProps): UseMemberOperationsReturn => {
  const handleRoleChange = useCallback(
    async (memberId: string, memberRole: OrganizationRoleKey): Promise<void> => {
      const membership = memberships?.data?.find((m) => m.id === memberId)
      if (!membership) return

      try {
        const previousRole = membership.role
        await membership.update({ role: memberRole })
        await memberships?.revalidate?.()
        trackEvent('member_role_changed', { fromRole: previousRole, toRole: memberRole })
        toast.success('Member role updated')
      } catch (error) {
        logger.error('Failed to update member role:', error)
        toast.error('Failed to update member role')
      }
    },
    [memberships]
  )

  const handleRemoveMember = useCallback(
    async (memberId: string): Promise<void> => {
      const membership = memberships?.data?.find((m) => m.id === memberId)
      if (!membership) return

      if (membership.publicUserData?.userId === currentUserId) {
        toast.error('You cannot remove yourself from the organization')
        return
      }

      try {
        await membership.destroy()
        await memberships?.revalidate?.()
        trackEvent('member_removed', {})
        toast.success('Member removed from organization')
      } catch (error) {
        logger.error('Failed to remove member:', error)
        toast.error('Failed to remove member')
      }
    },
    [memberships, currentUserId]
  )

  return { handleRoleChange, handleRemoveMember }
}
